import { useEffect, type RefObject } from 'react';

interface UsePinPopoverInteractionsOptions {
  isEditing: boolean;
  hasExistingLabel: boolean;
  onClose: () => void;
  popoverRef: RefObject<HTMLDivElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  setIsMenuOpen: (open: boolean) => void;
  setIsEditing: (editing: boolean) => void;
}

export function usePinPopoverInteractions({
  isEditing,
  hasExistingLabel,
  onClose,
  popoverRef,
  menuRef,
  textareaRef,
  setIsMenuOpen,
  setIsEditing,
}: UsePinPopoverInteractionsOptions) {
  useEffect(() => {
    if (!isEditing) return;
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    // Land the caret at the end of the existing text instead of the browser
    // default (start of the field).
    const end = textarea.value.length;
    textarea.setSelectionRange(end, end);
  }, [isEditing, textareaRef]);

  useEffect(() => {
    function handleKey(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (isEditing && hasExistingLabel) {
        setIsEditing(false);
      } else {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [hasExistingLabel, isEditing, onClose, setIsEditing]);

  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        !(target as Element).closest?.('[data-emoji-picker-portal]')
      ) {
        onClose();
      }
    }
    window.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      window.removeEventListener('pointerdown', handlePointerDown, true);
  }, [menuRef, onClose, popoverRef, setIsMenuOpen]);
}
